export const AI_IMAGE_TYPE_OPTIONS = [
  {
    label: "Image",
    value: "image",
    suggestionLabel: "Generate image",
  },
  {
    label: "Infographic",
    value: "infographic",
  },
  {
    label: "Diagram",
    value: "diagram",
  },
  {
    label: "Illustration",
    value: "illustration",
  },
  {
    label: "Icon",
    value: "icon",
  },
];

export const AI_IMAGE_MODELS = [
  {
    label: "Basic model",
    name: "Basic",
    value: "basic",
    description: "Faster, less accurate",
  },
  {
    label: "Pro model",
    name: "Pro",
    value: "pro",
    description: "Slower, better results",
  },
];

export const DOTS_PATTERN = `data:image/svg+xml,%3Csvg width='20' height='20' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='dots' x='0' y='0' width='20' height='20' patternUnits='userSpaceOnUse'%3E%3Ccircle cx='2' cy='2' r='1' fill='%23D0CDD8'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23dots)'/%3E%3C/svg%3E`;
export const MAX_REFERENCE_IMAGES = 4;
export const MAX_REFERENCE_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
// ============================================================================
// DIAGRAM CONSTANTS
// ============================================================================

const DIAGRAM_CONFIG = {
  styles: [
    {
      label:
        "Minimal monochrome diagram, thin strokes, lots of whitespace, flat vector, high legibility",
      value: "minimal-monochrome",
      image: "/diagrams/1.webp",
      shortLabel: "Minimal Monochrome",
    },
    {
      label:
        "Modern SaaS-style diagram, rounded rectangles, subtle shadows, clean spacing, flat vector, high legibility",
      value: "modern-saas-style",
      image: "/diagrams/2.webp",
      shortLabel: "Modern SaaS",
    },
    {
      label:
        "Bold outline diagram, high contrast, thick strokes, simple shapes, flat vector, high legibility",
      value: "bold-outline",
      image: "/diagrams/3.webp",
      shortLabel: "Bold Outline",
    },
    {
      label:
        "Color-coded sections diagram, soft fills, clear grouping, consistent icons, flat vector, high legibility",
      value: "color-coded-sections",
      image: "/diagrams/4.webp",
      shortLabel: "Color Coded",
    },
    {
      label:
        "Blueprint / technical schematic diagram, fine lines, precise geometry, grid alignment, high legibility",
      value: "blueprint-technical-schematic",
      image: "/diagrams/5.webp",
      shortLabel: "Blueprint",
    },
    {
      label:
        "Whiteboard sketch diagram, hand-drawn line style, still neat and legible, clean spacing, high contrast",
      value: "whiteboard-sketch",
      image: "/diagrams/6.webp",
      shortLabel: "Whiteboard Sketch",
    },
    {
      label:
        "Isometric diagram, clean perspective, minimal detail, crisp edges, modern look",
      value: "isometric",
      image: "/diagrams/7.webp",
      shortLabel: "Isometric",
    },
    {
      label:
        "Cards-based diagram, modular blocks, subtle gradient fills, modern UI look, clean spacing, high legibility",
      value: "cards-based",
      image: "/diagrams/8.webp",
      shortLabel: "Cards Based",
    },
    {
      label:
        "Sharp geometric diagram, flat fills, strong alignment, crisp corners, high contrast, high legibility",
      value: "sharp-geometric",
      image: "/diagrams/9.webp",
      shortLabel: "Sharp Geometric",
    },
  ],

  variants: [
    { label: "Flow/process", value: "flow-process" },
    { label: "Cycle", value: "cycle" },
    { label: "Hierarchy", value: "hierarchy" },
    { label: "Timeline", value: "timeline" },
    { label: "Comparison", value: "comparison" },
    { label: "Matrix (2x2)", value: "matrix-2x2" },
    { label: "Funnel", value: "funnel" },
    { label: "Hub-and-spoke", value: "hub-and-spoke" },
    { label: "Venn", value: "venn" },
  ],

  layouts: [
    { label: "Left-to-right", value: "left-to-right" },
    { label: "Right-to-left", value: "right-to-left" },
    { label: "Top-to-bottom", value: "top-to-bottom" },
    { label: "Circular", value: "circular" },
    { label: "Centered", value: "centered" },
    { label: "Two-column", value: "two-column" },
    { label: "2x2 grid", value: "2x2-grid" },
  ],

  aspectRatios: [
    { label: "Square", value: "square", ratio: "1:1" },
    { label: "Widescreen", value: "widescreen", ratio: "16:9" },
    { label: "Vertical", value: "vertical", ratio: "9:16" },
  ],
} as const;

// ============================================================================
// IMAGE CONSTANTS
// ============================================================================

const IMAGE_CONFIG = {
  styles: [
    {
      label: "Photorealistic, natural textures, professional commercial photo",
      value: "photorealistic",
      image: "/images/1.webp",
      shortLabel: "Photorealistic",
    },
    {
      label:
        "Cinematic photorealism, dramatic lighting, shallow depth of field",
      value: "cinematic-photorealism",
      image: "/images/2.webp",
      shortLabel: "Cinematic",
    },
    {
      label: "Studio product photo look, clean background, softbox lighting",
      value: "studio-product-photo",
      image: "/images/3.webp",
      shortLabel: "Studio Product",
    },
    {
      label: "3D render, clean modern materials, soft lighting, high detail",
      value: "3d-render",
      image: "/images/4.webp",
      shortLabel: "3D Render",
    },
    {
      label: "3D stylized, smooth shapes, modern, clean background",
      value: "3d-stylized",
      image: "/images/5.webp",
      shortLabel: "3D Stylized",
    },
    {
      label: "Digital illustration, semi-realistic, clean shapes, modern",
      value: "digital-illustration",
      image: "/images/6.webp",
      shortLabel: "Digital Illustration",
    },
    {
      label: "Hand-drawn sketch style, pencil/ink look, clean lines (no text)",
      value: "hand-drawn-sketch",
      image: "/images/7.webp",
      shortLabel: "Hand-drawn Sketch",
    },
    {
      label:
        "Painterly illustration, soft brushwork, rich but controlled detail",
      value: "painterly-illustration",
      image: "/images/8.webp",
      shortLabel: "Painterly",
    },
    {
      label: "Minimal abstract, simple geometric forms, modern gradients",
      value: "minimal-abstract",
      image: "/images/9.webp",
      shortLabel: "Minimal Abstract",
    },
  ],

  aspectRatios: [
    { label: "Square", value: "square", ratio: "1:1" },
    { label: "Widescreen", value: "widescreen", ratio: "16:9" },
    { label: "Vertical", value: "vertical", ratio: "9:16" },
    { label: "Standard", value: "standard", ratio: "4:3" },
    { label: "Landscape", value: "landscape", ratio: "3:2" },
  ],
} as const;

// ============================================================================
// INFOGRAPHIC CONSTANTS
// ============================================================================

const INFOGRAPHIC_CONFIG = {
  styles: [
    {
      label:
        "Modern SaaS infographic, clean UI look, rounded cards, crisp icons",
      value: "modern-saas-infographic",
      image: "/infographics/1.webp",
      shortLabel: "Modern SaaS",
    },
    {
      label:
        "Bold editorial infographic, high contrast, striking shapes, punchy hierarchy",
      value: "bold-editorial-infographic",
      image: "/infographics/2.webp",
      shortLabel: "Bold Editorial",
    },
    {
      label:
        "Playful flat illustration infographic, friendly icons, bright but controlled",
      value: "playful-flat-illustration-infographic",
      image: "/infographics/3.webp",
      shortLabel: "Playful Flat",
    },
    {
      label:
        "Minimal monochrome infographic, strong spacing, simple icons, elegant",
      value: "minimal-monochrome-infographic",
      image: "/infographics/4.webp",
      shortLabel: "Minimal Monochrome",
    },
    {
      label: "Geometric infographic, sharp alignment, color blocks, modern",
      value: "geometric-infographic",
      image: "/infographics/5.webp",
      shortLabel: "Geometric",
    },
    {
      label: "Gradient modern infographic, subtle gradients, soft depth, clean",
      value: "gradient-modern-infographic",
      image: "/infographics/6.webp",
      shortLabel: "Gradient Modern",
    },
    {
      label:
        "Hand-drawn infographic, sketchy icons, human feel (still very legible)",
      value: "hand-drawn-infographic",
      image: "/infographics/7.webp",
      shortLabel: "Hand-drawn",
    },
    {
      label:
        "Data-forward infographic, charts-first, minimal decoration, clear labels",
      value: "data-forward-infographic",
      image: "/infographics/8.webp",
      shortLabel: "Data-forward",
    },
    {
      label:
        "Premium infographic, muted palette, refined icons, elegant spacing",
      value: "premium-infographic",
      image: "/infographics/9.webp",
      shortLabel: "Premium",
    },
  ],

  variants: [
    { label: "Steps / checklist", value: "steps-checklist" },
    { label: "Comparison", value: "comparison" },
    { label: "Timeline / roadmap", value: "timeline-roadmap" },
    { label: "Stats / key metrics", value: "stats-key-metrics" },
    {
      label: "Framework (2x2 / pillars / layers)",
      value: "framework-2x2-pillars-layers",
    },
    { label: "Before vs after", value: "before-vs-after" },
    { label: "Do / don't", value: "do-dont" },
    { label: "Myth vs fact", value: "myth-vs-fact" },
    { label: "Funnel", value: "funnel" },
    { label: "Map / journey", value: "map-journey" },
  ],

  layouts: [
    { label: "Left-to-right", value: "left-to-right" },
    { label: "Right-to-left", value: "right-to-left" },
    { label: "Top-to-bottom", value: "top-to-bottom" },
    { label: "Two-column", value: "two-column" },
    { label: "Three-column", value: "three-column" },
    { label: "Centered", value: "centered" },
    { label: "2x2 grid", value: "2x2-grid" },
  ],

  aspectRatios: [
    { label: "Square", value: "square", ratio: "1:1" },
    { label: "Widescreen", value: "widescreen", ratio: "16:9" },
    { label: "Vertical", value: "vertical", ratio: "9:16" },
    { label: "Standard", value: "standard", ratio: "4:3" },
  ],
} as const;

// ============================================================================
// ICON CONSTANTS
// ============================================================================

const ICON_CONFIG = {
  styles: [
    {
      label: "Outline icon, thin stroke, rounded caps, minimal",
      value: "outline-icon-thin-stroke-rounded-caps-minimal",
      image: "/icons/1.webp",
      shortLabel: "Minimal Outline",
    },
    {
      label: "Outline icon, medium stroke, sharp corners, technical",
      value: "outline-icon-medium-stroke-sharp-corners-technical",
      image: "/icons/2.webp",
      shortLabel: "Technical Outline",
    },
    {
      label: "Solid glyph icon, bold, simple silhouette",
      value: "solid-glyph-icon-bold-simple-silhouette",
      image: "/icons/3.webp",
      shortLabel: "Solid Glyph",
    },
    {
      label: "Duotone icon, two-color fill, clean shapes",
      value: "duotone-icon-two-color-fill-clean-shapes",
      image: "/icons/4.webp",
      shortLabel: "Duotone",
    },
    {
      label: "Flat colored icon, simple fills, minimal detail",
      value: "flat-colored-icon-simple-fills-minimal-detail",
      image: "/icons/5.webp",
      shortLabel: "Flat Color",
    },
    {
      label: "Rounded filled icon, soft friendly shapes",
      value: "rounded-filled-icon-soft-friendly-shapes",
      image: "/icons/6.webp",
      shortLabel: "Rounded Filled",
    },
    {
      label: "Isometric icon, subtle depth, clean geometry",
      value: "isometric-icon-subtle-depth-clean-geometry",
      image: "/icons/7.webp",
      shortLabel: "Isometric",
    },
    {
      label: "3D icon, soft lighting, minimal detail (still icon-like)",
      value: "3d-icon-soft-lighting-minimal-detail-still-icon-like",
      image: "/icons/8.webp",
      shortLabel: "Soft 3D",
    },
    {
      label: "Sticker icon, bold outline, simple fill, playful",
      value: "sticker-icon-bold-outline-simple-fill-playful",
      image: "/icons/9.webp",
      shortLabel: "Sticker",
    },
    {
      label: "Line + fill hybrid icon, crisp outline with small solid accents",
      value: "line-fill-hybrid-icon-crisp-outline-with-small-solid-accents",
      image: "/icons/10.webp",
      shortLabel: "Line Hybrid",
    },
  ],

  views: [
    { label: "Front view", value: "front-view" },
    { label: "Side view", value: "side-view" },
    { label: "Top-down view", value: "top-down-view" },
    { label: "Isometric view", value: "isometric-view" },
  ],

  backgrounds: [
    { label: "Plain solid background", value: "plain-solid-background" },
    { label: "Soft rounded square badge", value: "soft-rounded-square-badge" },
    { label: "Circle badge", value: "circle-badge" },
    {
      label: "Subtle gradient background (very light)",
      value: "subtle-gradient-background-very-light",
    },
  ],
} as const;

// ============================================================================
// ILLUSTRATION CONSTANTS
// ============================================================================

const ILLUSTRATION_CONFIG = {
  styles: [
    {
      label: "Flat modern illustration, clean shapes, minimal shading",
      value: "flat-modern-illustration-clean-shapes-minimal-shading",
      image: "/illustrations/1.webp",
      shortLabel: "Modern Flat",
    },
    {
      label: "Soft gradient illustration, modern, subtle depth",
      value: "soft-gradient-illustration-modern-subtle-depth",
      image: "/illustrations/2.webp",
      shortLabel: "Soft Gradient",
    },
    {
      label: "Editorial illustration, bold shapes, high contrast",
      value: "editorial-illustration-bold-shapes-high-contrast",
      image: "/illustrations/3.webp",
      shortLabel: "Bold Editorial",
    },
    {
      label: "Hand-drawn ink illustration, clean lines, minimal",
      value: "hand-drawn-ink-illustration-clean-lines-minimal",
      image: "/illustrations/4.webp",
      shortLabel: "Hand-drawn",
    },
    {
      label: "Watercolor illustration, soft washes, gentle texture",
      value: "watercolor-illustration-soft-washes-gentle-texture",
      image: "/illustrations/5.webp",
      shortLabel: "Watercolor",
    },
    {
      label: "3D illustration (not photoreal), smooth shapes, soft lighting",
      value: "3d-illustration-not-photoreal-smooth-shapes-soft-lighting",
      image: "/illustrations/6.webp",
      shortLabel: "3D Stylized",
    },
    {
      label: "Isometric illustration, clean geometry, modern palette",
      value: "isometric-illustration-clean-geometry-modern-palette",
      image: "/illustrations/7.webp",
      shortLabel: "Isometric",
    },
    {
      label:
        "Character-based illustration, friendly and professional, simple backgrounds",
      value:
        "character-based-illustration-friendly-and-professional-simple-backgrounds",
      image: "/illustrations/8.webp",
      shortLabel: "Character",
    },
  ],
} as const;

export const OPTIONS_CONFIG = {
  diagram: DIAGRAM_CONFIG,
  image: IMAGE_CONFIG,
  infographic: INFOGRAPHIC_CONFIG,
  icon: ICON_CONFIG,
  illustration: ILLUSTRATION_CONFIG,
};
